/*
 * cc-cart-cross-sell.js
 * Comportement du module de cross-sell discret du panier (drawer + page panier).
 *
 * Pourquoi un asset chargé globalement plutôt qu'un <script> dans le snippet :
 * le drawer (et donc le module) apparaît le plus souvent via la Section Rendering
 * API (morph) — ex. panier vide au chargement, puis ajout produit -> auto-open.
 * Or `morph` NE réexécute PAS les <script> insérés (cf. assets/morph.js,
 * recreateAppBlockScripts ne touche que les app blocks). On lie donc UN listener
 * délégué sur `document`, une seule fois, au chargement initial : il survit à tous
 * les re-render et capte les boutons recréés par le morph.
 *
 * Pipeline d'ajout/refresh : identique au module upsell produit existant
 * (blocks/_cc-upsell.liquid) -> POST /cart/add.js, puis fetch /cart.js, puis
 * dispatch d'un évènement natif `cart:update`. cart-items-component (drawer +
 * page) ré-render alors sa section via la Section Rendering API et le produit
 * ajouté disparaît du module tout seul. Aucun système de panier parallèle.
 */
(() => {
  if (window.__ccCartXsellBound) return;
  window.__ccCartXsellBound = true;

  const CART_ADD_URL =
    (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  const CART_URL = '/cart.js';

  /** @param {HTMLButtonElement} button */
  const setLoading = (button, loading) => {
    if (loading) {
      button.dataset.loading = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      delete button.dataset.loading;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = /** @type {HTMLButtonElement | null} */ (
      target.closest('[data-cc-xsell-add]')
    );
    if (!button) return;

    event.preventDefault();

    // Anti double-clic / re-entrance.
    if (button.disabled || button.dataset.loading === 'true') return;

    const variantId = parseInt(button.getAttribute('data-variant-id') || '', 10);
    if (!variantId) return;

    setLoading(button, true);

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then(() => fetch(CART_URL).then((response) => response.json()))
      .then((cart) => {
        // Réutilise le chemin de rafraîchissement natif du thème : cart-drawer.js
        // (#handleCartAdd -> auto-open) et component-cart-items.js (#handleCartUpdate
        // -> sectionRenderer.renderSection) écoutent `cart:update` sur document.
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'cc-cart-cross-sell',
              data: {
                source: 'cc-cart-cross-sell',
                itemCount: cart && cart.item_count,
                variantId: variantId,
              },
            },
          })
        );

        // Le re-render retire ce bouton du DOM. Défensif si le morph le conserve.
        if (button.isConnected) setLoading(button, false);
      })
      .catch(() => {
        // Erreur : on ré-active, pas de crash, pas de redirection.
        setLoading(button, false);
        button.dataset.error = 'true';
        setTimeout(() => {
          delete button.dataset.error;
        }, 2500);
      });
  });
})();
